import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Plus, Search, Download, Eye, Trash2, Loader2, Pencil, Calendar } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ClinicalRecordList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const { data: clinicalRecords, isLoading } = useQuery({
    queryKey: ["clinical-records"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("clinical_records")
        .select(`
          *,
          patients_onboarding!inner(full_name, data_nascimento)
        `)
        .eq("professional_id", user.id)
        .order("visit_date", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (recordId: string) => {
      const { error } = await supabase
        .from("clinical_records")
        .delete()
        .eq("id", recordId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinical-records"] });
      toast.success("Prontuário excluído com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao excluir prontuário");
    },
  });

  const filteredRecords = clinicalRecords?.filter((record) => {
    const matchesSearch = 
      record.patients_onboarding?.full_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      record.chief_complaint?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || record.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusColors: Record<string, string> = {
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    archived: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  };

  const statusLabels: Record<string, string> = {
    draft: "Rascunho",
    completed: "Concluído",
    archived: "Arquivado",
  };

  const visitTypeLabels: Record<string, string> = {
    consultation: "Consulta",
    follow_up: "Retorno",
    emergency: "Emergência",
    procedure: "Procedimento",
    other: "Outro",
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500">
            <ClipboardList className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Prontuário Atendimento</h1>
            <p className="text-muted-foreground">
              Gerencie prontuários clínicos dos seus pacientes
            </p>
          </div>
        </div>

        <Button
          onClick={() => navigate("/app/aplicativo/clinicalrecord/novo")}
          className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Prontuário
        </Button>
      </div>

      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-700 dark:text-blue-300">
            Meus Prontuários
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 mb-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por paciente ou queixa principal..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={statusFilter === null ? "default" : "outline"}
                onClick={() => setStatusFilter(null)}
                size="sm"
                className="flex-1 sm:flex-none"
              >
                Todos
              </Button>
              <Button
                variant={statusFilter === "draft" ? "default" : "outline"}
                onClick={() => setStatusFilter("draft")}
                size="sm"
                className="flex-1 sm:flex-none"
              >
                Rascunhos
              </Button>
              <Button
                variant={statusFilter === "completed" ? "default" : "outline"}
                onClick={() => setStatusFilter("completed")}
                size="sm"
                className="flex-1 sm:flex-none"
              >
                Concluídos
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : filteredRecords && filteredRecords.length > 0 ? (
            <>
              {/* Mobile: Cards */}
              <div className="sm:hidden space-y-3">
                {filteredRecords.map((record) => (
                  <Card key={record.id} className="p-4 border-blue-200 transition-all hover:shadow-md">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-foreground">
                          {record.patients_onboarding?.full_name}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {record.chief_complaint || "Sem queixa principal"}
                        </p>
                      </div>
                      <Badge className={statusColors[record.status || "draft"]}>
                        {statusLabels[record.status || "draft"]}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(record.visit_date), "dd/MM/yyyy", {
                        locale: ptBR,
                      })}
                      <span className="mx-1">•</span>
                      {visitTypeLabels[record.visit_type || "consultation"]}
                    </div>
                    <div className="flex gap-2">
                      {record.status === "draft" && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() =>
                            navigate(`/app/aplicativo/clinicalrecord/${record.id}/editar`)
                          }
                        >
                          <Pencil className="h-4 w-4 mr-1" /> Editar
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() =>
                          navigate(`/app/aplicativo/clinicalrecord/${record.id}`)
                        }
                      >
                        <Eye className="h-4 w-4 mr-1" /> Ver
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir este prontuário? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteMutation.mutate(record.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Desktop: Table */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Paciente</TableHead>
                      <TableHead>Data do Atendimento</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Queixa Principal</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecords.map((record) => (
                      <TableRow key={record.id} className="transition-colors hover:bg-accent/50">
                        <TableCell className="font-medium">
                          {record.patients_onboarding?.full_name}
                        </TableCell>
                        <TableCell>
                          {format(new Date(record.visit_date), "dd/MM/yyyy", {
                            locale: ptBR,
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {visitTypeLabels[record.visit_type || "consultation"]}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {record.chief_complaint || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[record.status || "draft"]}>
                            {statusLabels[record.status || "draft"]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {record.status === "draft" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  navigate(`/app/aplicativo/clinicalrecord/${record.id}/editar`)
                                }
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(`/app/aplicativo/clinicalrecord/${record.id}`)
                              }
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja excluir este prontuário? Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteMutation.mutate(record.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhum prontuário encontrado</p>
              <Button
                onClick={() => navigate("/app/aplicativo/clinicalrecord/novo")}
                className="mt-4"
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar primeiro prontuário
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
